import { FC, useState, useMemo } from 'react';
import { Button } from 'react-daisyui';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/context/AuthContext';
import useCreateMixedPracticeSet, { CategoryRequest } from '@/api/useCreateMixedPracticeSet';
import useQuestionCategories, { buildCategoryLabelMap } from '@/api/useQuestionCategories';
import DailyLimitModal from '@/components/ui/DailyLimitModal/DailyLimitModal';

export interface WeakCategory {
  category: string;
  percentage: number;
  totalScore: number;
  maxScore: number;
  count: number;
}

interface AreasToImproveCardProps {
  weakCategories: WeakCategory[];
  subject?: string | null;
  paperNumber?: string | null;
}

const AreasToImproveCard: FC<AreasToImproveCardProps> = ({ weakCategories, subject, paperNumber }) => {
  const navigate = useNavigate();
  const { session } = useAuthContext();
  const createMixedPracticeSetMutation = useCreateMixedPracticeSet();
  const [limitError, setLimitError] = useState<string | null>(null);

  // Fetch category labels from database
  const { data: categories = [] } = useQuestionCategories();

  // Build category label map for this paper
  const categoryLabels = useMemo(
    () => buildCategoryLabelMap(categories, subject, paperNumber),
    [categories, subject, paperNumber]
  );

  // Build a map from category (e.g., 'Q1') to category_code (e.g., 'LANG-P1-Q1') for this paper
  const categoryToCategoryCode = useMemo(() => {
    const map: Record<string, string> = {};
    categories
      .filter((c) => (!subject || c.subject === subject) && (!paperNumber || c.paper_number === paperNumber))
      .forEach((c) => {
        map[c.category] = c.category_code;
      });
    return map;
  }, [categories, subject, paperNumber]);

  const getCategoryLabel = (category: string): string => {
    return categoryLabels[category] || category;
  };

  // Deduplicate weak categories by display label, merging scores for same-label entries
  const deduplicatedWeakCategories = useMemo(() => {
    const mergedMap = new Map<string, WeakCategory>();

    weakCategories.forEach((cat) => {
      const label = getCategoryLabel(cat.category);
      const existing = mergedMap.get(label);

      if (existing) {
        const merged: WeakCategory = {
          ...existing,
          totalScore: existing.totalScore + cat.totalScore,
          maxScore: existing.maxScore + cat.maxScore,
          count: existing.count + cat.count,
          percentage: 0,
        };
        merged.percentage = merged.maxScore > 0 ? (merged.totalScore / merged.maxScore) * 100 : 0;
        mergedMap.set(label, merged);
      } else {
        mergedMap.set(label, { ...cat });
      }
    });

    return Array.from(mergedMap.values()).sort((a, b) => a.percentage - b.percentage);
  }, [weakCategories, getCategoryLabel]);

  // Filter to categories that have a category_code mapping for this paper.
  // Availability of actual questions is handled by the edge function at request time.
  const practiceableCategories = useMemo(() => {
    return deduplicatedWeakCategories.filter((cat) => !!categoryToCategoryCode[cat.category]);
  }, [deduplicatedWeakCategories, categoryToCategoryCode]);

  const practiceAvailable = practiceableCategories.length > 0;

  const handleGeneratePracticeSet = async () => {
    if (!session?.user?.id) return;

    setLimitError(null);

    try {
      // Prepare category requests - 3 questions per each weak category
      const categoriesToPractice = practiceableCategories.slice(0, 3);
      const questionsPerCategory = 3;

      const categoryRequests: CategoryRequest[] = categoriesToPractice.map((cat) => ({
        category_code: categoryToCategoryCode[cat.category],
        question_count: questionsPerCategory,
      }));

      const result = await createMixedPracticeSetMutation.mutateAsync({
        authId: session.user.id,
        categories: categoryRequests,
      });

      navigate(`/practice-sets/${result.practice_set_id}`);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create practice set';
      setLimitError(errorMessage);
    }
  };

  if (weakCategories.length === 0) {
    return null;
  }

  return (
    <>
      <div className="bg-orange-50 border-2 border-orange-200 rounded-[20px] p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Areas to Improve</h3>
            <p className="text-gray-700 mb-3">
              Based on your performance, we've identified some question types where you could improve:
            </p>

            {/* Top weak categories */}
            <div className="space-y-2 mb-4">
              {deduplicatedWeakCategories.slice(0, 3).map((category) => (
                <div key={category.category} className="flex items-center justify-between bg-white rounded-lg p-3">
                  <div>
                    <span className="font-semibold text-gray-900">{getCategoryLabel(category.category)}</span>
                    <span className="text-sm text-gray-600 ml-2">
                      ({category.count} question{category.count !== 1 ? 's' : ''})
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">
                      {category.totalScore}/{category.maxScore}
                    </span>
                    <span className="font-bold text-orange-600">{Math.round(category.percentage)}%</span>
                  </div>
                </div>
              ))}
            </div>

            {practiceAvailable ? (
              <>
                <p className="text-gray-700 mb-4">
                  We can generate a personalized practice set focusing on these areas to help you improve.
                </p>

                <Button
                  size="md"
                  onClick={handleGeneratePracticeSet}
                  className="bg-orange-500 hover:bg-orange-600 border-none text-white"
                  disabled={createMixedPracticeSetMutation.isPending}
                >
                  {createMixedPracticeSetMutation.isPending ? 'Generating...' : 'Generate Practice Set'}
                </Button>
              </>
            ) : (
              <p className="text-gray-600 text-sm italic">
                Practice sets for {subject || 'this subject'} {paperNumber || ''} are coming soon.
              </p>
            )}
          </div>
        </div>
      </div>

      {limitError && <DailyLimitModal errorMessage={limitError} onClose={() => setLimitError(null)} />}
    </>
  );
};

export default AreasToImproveCard;
